import Button from '@atoms/Button/Button';
import TextInput from '@atoms/TextInput/TextInput';
import Form from '@molecules/Form/Form';
import OpeningsSelector, {
  DOOR_AREA_SIZE,
  WINDOW_AREA_SIZE,
} from '@molecules/OpeningsSelector/OpeningsSelector';
import { Action, ActionType } from '@organisms/PaintCalculator/reducer';
import classNames from 'classnames';
import { Dispatch } from 'react';

export interface Wall {
  readonly length: string;
  readonly height: string;
  readonly width?: string;
}

export interface PaintCalculatorLabels {
  readonly addAnotherAreaCtaLabel: string;
  readonly addAnotherWallCtaLabel: string;
  readonly addDoorAndWindowsCtaLabel: string;
  readonly decreaseAriaLabel: string;
  readonly doorsLabel: string;
  readonly heightLabel: string;
  readonly heightOrWidthLabel: string;
  readonly increaseAriaLabel: string;
  readonly lengthLabel: string;
  readonly metricFeetLabel: string;
  readonly metricMeterLabel: string;
  readonly metricMeterSquareLabel: string;
  readonly removeAreaCtaLabel: string;
  readonly removeWallCtaLabel: string;
  readonly widthLabel: string;
  readonly windowsLabel: string;
}

export interface IWallsFormSection {
  activeTab: 'surface' | 'area';
  addWall: () => void;
  dispatch: Dispatch<Action>;
  doorsCount: number;
  labels: PaintCalculatorLabels;
  onSubmit: () => void;
  removeWall: () => void;
  showOpenings: boolean;
  unit: string;
  walls: Wall[];
  windowsCount: number;
}

const WallsFormSection = ({
  activeTab,
  addWall,
  dispatch,
  doorsCount,
  labels,
  onSubmit,
  removeWall,
  showOpenings,
  unit,
  walls,
  windowsCount,
}: Readonly<IWallsFormSection>) => {
  const {
    addAnotherAreaCtaLabel,
    addAnotherWallCtaLabel,
    addDoorAndWindowsCtaLabel,
    doorsLabel,
    heightLabel,
    heightOrWidthLabel,
    lengthLabel,
    metricFeetLabel,
    metricMeterLabel,
    metricMeterSquareLabel,
    removeAreaCtaLabel,
    removeWallCtaLabel,
    widthLabel,
    windowsLabel,
  } = labels;

  const isSurfaceTab = activeTab === 'surface';
  const suffixLabel = unit === 'm' ? metricMeterLabel : metricFeetLabel;

  const updateWall = (index: number, field: keyof Wall, value: string) => {
    // Only allow up to 2 decimal places
    const decimalPattern = /^\d*\.?\d{0,2}$/;

    if (value === '' || decimalPattern.test(value)) {
      const updatedWalls = walls.map((w, i) =>
        i === index ? { ...w, [field]: value } : w,
      );
      dispatch({ type: ActionType.SET_WALLS, payload: updatedWalls });
    }
  };

  return (
    <Form formSubmit={onSubmit}>
      <div className="tw-flex tw-flex-col tw-justify-start tw-gap-4 tw-pb-4">
        {walls.map((wall, id) => (
          <div
            key={id}
            className={classNames('tw-flex tw-flex-col tw-gap-6', {
              'md:tw-flex-row md:tw-gap-14': isSurfaceTab,
            })}
          >
            <div className="tw-flex tw-w-full tw-flex-row tw-gap-4">
              <TextInput
                id={`${lengthLabel}-${id}`}
                label={lengthLabel}
                type="number"
                displayLabel
                value={wall.length}
                suffix={suffixLabel}
                onChange={(e) => updateWall(id, 'length', e.target.value)}
                containerClasses="tw-w-full"
              />
              <TextInput
                id={
                  isSurfaceTab
                    ? `${heightOrWidthLabel}-${id}`
                    : `${heightLabel}-${id}`
                }
                label={isSurfaceTab ? heightOrWidthLabel : heightLabel}
                type="number"
                displayLabel
                value={wall.height}
                suffix={suffixLabel}
                onChange={(e) => updateWall(id, 'height', e.target.value)}
                containerClasses="tw-w-full"
              />
              {!isSurfaceTab && (
                <TextInput
                  id={`${widthLabel}-${id}`}
                  label={widthLabel}
                  type="number"
                  displayLabel
                  value={wall.width}
                  suffix={suffixLabel}
                  onChange={(e) => updateWall(id, 'width', e.target.value)}
                  containerClasses="tw-w-full"
                />
              )}
            </div>
          </div>
        ))}

        <div className="tw-mt-2 tw-flex tw-justify-between">
          {walls.length < 4 && (
            <Button
              onClick={addWall}
              label={
                isSurfaceTab ? addAnotherWallCtaLabel : addAnotherAreaCtaLabel
              }
              variant="link"
              buttonClasses="tw-self-start !tw-p-0"
              iconName="plus"
              contentClasses="tw-flex-row-reverse"
              iconClasses="!tw-w-[12px] !tw-h-[12px]"
            />
          )}

          {walls.length > 1 && (
            <Button
              onClick={removeWall}
              label={isSurfaceTab ? removeWallCtaLabel : removeAreaCtaLabel}
              variant="link"
              buttonClasses={classNames('tw-self-start !tw-p-0', {
                'tw-ml-auto': walls.length === 4,
              })}
              iconName="cross"
              iconClasses="!tw-w-[12px] !tw-h-[12px]"
            />
          )}
        </div>

        {showOpenings ? (
          <OpeningsSelector
            doorsLabel={doorsLabel}
            windowsLabel={windowsLabel}
            increaseAriaLabel={labels.increaseAriaLabel}
            decreaseAriaLabel={labels.decreaseAriaLabel}
            metricMeterSquareLabel={metricMeterSquareLabel}
            initialDoors={doorsCount}
            initialWindows={windowsCount}
            doorAreaSize={DOOR_AREA_SIZE}
            windowAreaSize={WINDOW_AREA_SIZE}
            onChangeDoors={(count) =>
              dispatch({
                type: ActionType.SET_DOORS_COUNT,
                payload: Number(count),
              })
            }
            onChangeWindows={(count) =>
              dispatch({
                type: ActionType.SET_WINDOWS_COUNT,
                payload: Number(count),
              })
            }
          />
        ) : (
          <Button
            onClick={() =>
              dispatch({ type: ActionType.SET_SHOW_OPENINGS, payload: true })
            }
            label={addDoorAndWindowsCtaLabel}
            variant="link"
            iconName="plus"
            buttonClasses="tw-self-start !tw-p-0"
            contentClasses="tw-flex-row-reverse"
            iconClasses="!tw-w-[12px] !tw-h-[12px]"
          />
        )}
      </div>
    </Form>
  );
};

export default WallsFormSection;
